import React, { useContext, useEffect, useState, useRef } from "react";
import CartItems from "~/assets/js/components/CartItems";
import Cutlery from "~/assets/js/components/Cutlery";
import FreeDeliveryNotification from "./FreeDeliveryNotification";
import CheckoutSummary from "~/assets/js/components/CheckoutSummary";
import { getComplementaryItems } from "~/assets/js/Api/storeApi";
import ComplementaryProducts from "~/assets/js/components/ComplementaryProducts";
import StickyFooter from "~/assets/js/components/StickyFooter";
import { CheckoutContext, ACTIONS, PAGES } from "../../CheckoutProvider";
import i18n from "~/assets/js/Helpers/i18n";
import isEmpty from "lodash.isempty";
import { DesktopGrid } from "~/assets/js/components/Layout/DesktopGrid";
import Headline from "~/assets/js/components/Typography/Headline";
import { useCartContext } from "~/assets/js/global/cartContext";
import { viewCart } from "~/assets/js/Api/eventsApi";
import ReusableBowlBox from "~/assets/js/components/ReusableBowls/ReusableBowlBox";
import { BOWLS_TYPES } from "~/assets/js/components/ReusableBowls/BowlsTypes";
import { wait } from "~/assets/js/Helpers/util";
import useForceRerender from "~/assets/js/Helpers/hooks/useForceRerender";
import FastCheckoutPopup from "~/assets/js/components/FastCheckout/FastCheckoutPopup";
import { useUserOrders } from "~/assets/js/Api/userApi";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import useFeatureFlags from "~/assets/js/Helpers/hooks/useFeatureFlags";
import CreateGroupOrder from "~/assets/js/components/CreateGroupOrder";
import useBodyScrollLock from "~/assets/js/Helpers/hooks/useBodyScrollLock";
import CreateGroup from "./CreateGroup";
import useGroupOrder, {
    GROUP_ORDER_BUFFER,
} from "~/assets/js/global/useGroupOrder";
import GroupParticipant from "~/assets/js/components/GroupParticipant";
import GroupCartActions from "~/assets/js/components/GroupCartActions";
import JoinGroupOrder from "~/assets/js/components/JoinGroupOrder/JoinGroupOrder";
import CancelGroupOrderBanner from "~/assets/js/components/CancelGroupOrderBanner";
import EmojiConfirmPopup from "~/assets/js/components/EmojiConfirmPopup";
import TimeslotPopup from "~/assets/js/components/TimeslotPopup";

const [DESSERTS_SLUG, DRINKS_SLUG, DIPS_SLUG] = [
    "desserts",
    "drinks",
    "bread_dips",
];

const Cart = () => {
    const { cart, cartItems, actions } = useCartContext();
    const desserts = getComplementaryItems(DESSERTS_SLUG);
    const drinks = getComplementaryItems(DRINKS_SLUG);
    const dips = getComplementaryItems(DIPS_SLUG);
    const checkoutContext = useContext(CheckoutContext);
    const hasInitCart = useRef(false);
    const rerenderComponent = useForceRerender();
    const { reusableBowls } = checkoutContext.state;
    const { isDesktop } = useViewport();
    const { FEATURE_FAST_CHECKOUT, FEATURE_GROUP_ORDER } = useFeatureFlags();
    const {
        groupCartData,
        isParticipant,
        commitParticipant,
        leaveParticipant,
        isGroupOrderURL,
        setIsGroupOrderURL,
        isGroupOrder,
        isOwner,
        memberGroupOrderTimeOut,
        setMemberGroupOrderTimeOut,
        setOwnerGroupOrderTimeOut,
        ownerGroupOrderTimeOut,
        timeslotPopupOpen,
        setOpenTimeslotPopup,
        earliestTimeslot,
        ownerChangeTimeSlot,
        onTimeslotUpdate,
        abortGroupOrder,
    } = useGroupOrder();
    const [openFastCheckout, setOpenFastCheckout] = useState(false);
    const [openGroupOrder, setOpenGroupOrder] = useState(false);
    const [cancelGroupOrderState, setCancelGroupOrderState] = useState(false);
    const { orders: previousOrders, isLoading: ordersLoading } =
        useUserOrders();

    const isInitialMount = useRef(true);
    useBodyScrollLock(openGroupOrder);

    const shouldInitFastCheckout =
        FEATURE_FAST_CHECKOUT &&
        !isDesktop &&
        isInitialMount.current &&
        !ordersLoading;

    const fullPrice = cart?.value_gross || 0;
    const itemsPrice =
        cart?.items?.reduce((total, item) => {
            return total + parseFloat(item?.value_gross);
        }, 0) || 0;

    const cartPrice =
        itemsPrice +
        cart?.energy_fee?.energy_fee_items.reduce(
            (total, currentFee) => total + parseFloat(currentFee.value_gross),
            0
        );
    const freeDeliveryThreshold = cart?.shipping?.free_limit
        ? parseInt(cart.shipping.free_limit)
        : 0;
    const isBelowMinimumOrder =
        parseFloat(cart?.shipping?.minimal_value ?? 0) > itemsPrice;

    useEffect(() => {
        if (isEmpty(cart) || hasInitCart.current) {
            return;
        }

        viewCart(cart.uid);
        hasInitCart.current = true;
    }, [cart]);

    useEffect(() => {
        const rerender = () => {
            rerenderComponent();
        };
        window.addEventListener("focus", rerender);
        return () => {
            window.removeEventListener("focus", rerender);
        };
    }, []);

    const moveToNextPage = () => {
        const { user } = checkoutContext.state;
        let nextPage = PAGES.CONTACT_DETAILS;
        if (isEmpty(user)) {
            nextPage = PAGES.LOGIN;
        }

        return checkoutContext.dispatch({
            type: ACTIONS.CHANGE_PAGE,
            payload: nextPage,
        });
    };

    const handleCommitPaticipant = async () => {
        const response = await commitParticipant();
        if (response.ok) {
            return checkoutContext.dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.MEMBER_THANK_YOU,
            });
        }

        return checkoutContext.dispatch(
            AddToast("danger", "Something went wrong", "", () =>
                handleCommitPaticipant()
            )
        );
    };

    const toggleBowls = async () => {
        // TOGGLE SWITCH
        let result =
            reusableBowls === BOWLS_TYPES.CLASSIC
                ? BOWLS_TYPES.REUSABLE
                : BOWLS_TYPES.CLASSIC;

        checkoutContext.dispatch({
            type: ACTIONS.TOGGLE_REUSABLE_BOWLS,
            payload: result,
        });

        await wait(500);

        await actions.toggleReusableBowls(result);
        rerenderComponent();
    };

    const takeAddressInfoFromPreviousOrder = (orders) => {
        if (orders === undefined || !FEATURE_FAST_CHECKOUT) {
            return;
        }

        const ordersarr = Object.values(orders);
        const lastOrder = orders[ordersarr.length - 1];

        const data = {
            billingAddress: lastOrder.billing,
            deliveryAddress: lastOrder.shipping,
            sameAsBilling: lastOrder.billing,
            email: lastOrder.email,
            phone: lastOrder.phone,
            payment: lastOrder?.payment?.method,
            dispatch_type: lastOrder?.dispatch_type,
        };

        actions.updateFromPreviousOrder(data);
    };

    const renderComplementaryProducts = () => {
        return (
            <>
                <ComplementaryProducts
                    title="complementary.drinks"
                    products={drinks}
                />
                <ComplementaryProducts
                    title="complementary.desserts"
                    products={desserts}
                />
                <ComplementaryProducts
                    title="complementary.other"
                    products={dips}
                />
            </>
        );
    };

    const stickyFooterActions = (() => {
        if (FEATURE_GROUP_ORDER && isParticipant) {
            return {
                text: "memberGroupOrder.commitOrderButton",
                action: handleCommitPaticipant,
            };
        }

        if (!FEATURE_FAST_CHECKOUT) {
            return {
                text: undefined,
                action: moveToNextPage,
            };
        }

        return {
            text: isDesktop ? undefined : "cart.fastCheckout",
            action: () =>
                isDesktop ? moveToNextPage() : setOpenFastCheckout(true),
        };
    })();

    if (shouldInitFastCheckout) {
        takeAddressInfoFromPreviousOrder(previousOrders);
        isInitialMount.current = false;
    }

    return (
        <>
            {FEATURE_GROUP_ORDER && isGroupOrderURL && !isGroupOrder ? (
                <JoinGroupOrder
                    closeOverlay={() => setIsGroupOrderURL(false)}
                />
            ) : (
                <>
                    <DesktopGrid
                        leftSide={
                            <>
                                {!isParticipant ? (
                                    <Headline size={4} resizable>
                                        {i18n.t("cart.title")}
                                    </Headline>
                                ) : (
                                    <Headline size={4} resizable>
                                        {i18n.t("memberGroupOrder.title")}
                                    </Headline>
                                )}

                                {isParticipant && (
                                    <div className="group-cart-subTitle">
                                        {i18n.t("memberGroupOrder.subTitle")}
                                    </div>
                                )}

                                {!isParticipant && !isBelowMinimumOrder && (
                                    <FreeDeliveryNotification
                                        totals={cartPrice}
                                        threshold={freeDeliveryThreshold}
                                        label={"cart.freeDelivery.title"}
                                    />
                                )}

                                {!isParticipant && isBelowMinimumOrder && (
                                    <FreeDeliveryNotification
                                        totals={itemsPrice}
                                        threshold={parseFloat(
                                            cart?.shipping?.minimal_value ?? 0
                                        )}
                                        label={"cart.minimumOrderValue.title"}
                                    />
                                )}

                                {!isParticipant && <Cutlery />}
                                <CartItems cartItems={cartItems || []} />
                                {FEATURE_GROUP_ORDER && (
                                    <>
                                        {!isGroupOrder && (
                                            <CreateGroup
                                                onClick={() =>
                                                    setOpenGroupOrder(true)
                                                }
                                            />
                                        )}

                                        {isOwner && (
                                            <div className="group-cart-header">
                                                <Headline size={6}>
                                                    {i18n.t(
                                                        "Deine Gruppenbestellung"
                                                    )}
                                                </Headline>
                                                <GroupCartActions
                                                    cartUid={cart?.group_uid}
                                                />
                                            </div>
                                        )}

                                        {isOwner &&
                                            groupCartData?.participants &&
                                            groupCartData.participants.map(
                                                (participant) => (
                                                    <GroupParticipant
                                                        key={`gp-${participant.uid}`}
                                                        participant={{
                                                            name: participant.name,
                                                            uid: participant.uid,
                                                        }}
                                                        itemsCount={
                                                            participant
                                                                ?.cart_items
                                                                ?.length || 0
                                                        }
                                                    />
                                                )
                                            )}

                                        {openGroupOrder && (
                                            <CreateGroupOrder
                                                onBackClick={() =>
                                                    setOpenGroupOrder(false)
                                                }
                                            />
                                        )}
                                    </>
                                )}
                                {!isParticipant ? (
                                    <ReusableBowlBox
                                        bowlType={reusableBowls}
                                        onToggle={toggleBowls}
                                    />
                                ) : (
                                    <>
                                        <CancelGroupOrderBanner
                                            setCancelGroupOrder={
                                                setCancelGroupOrderState
                                            }
                                        />
                                        <EmojiConfirmPopup
                                            open={cancelGroupOrderState}
                                            setOpen={setCancelGroupOrderState}
                                            title={i18n.t(
                                                `memberGroupOrder.cancelGroupOrderPopup.title`
                                            )}
                                            infoText={i18n.t(
                                                `memberGroupOrder.cancelGroupOrderPopup.description`
                                            )}
                                            ctaText={i18n.t(
                                                `memberGroupOrder.cancelGroupOrderPopup.acceptButton`
                                            )}
                                            onConfirm={leaveParticipant}
                                        />
                                    </>
                                )}
                            </>
                        }
                        rightSide={
                            <CheckoutSummary
                                handleDesktopMoveForward={
                                    FEATURE_GROUP_ORDER && isParticipant
                                        ? handleCommitPaticipant
                                        : moveToNextPage
                                }
                                forwardButtonDisabled={
                                    (isBelowMinimumOrder && !isParticipant) ||
                                    !(cartItems?.length > 0)
                                }
                            />
                        }
                    />
                    <div id="complementary-products">
                        {renderComplementaryProducts()}
                    </div>
                </>
            )}

            <StickyFooter
                totals={isParticipant ? itemsPrice : fullPrice}
                onClick={stickyFooterActions.action}
                disabled={
                    (isBelowMinimumOrder && !isParticipant) ||
                    !(cartItems?.length > 0)
                }
                CTA={stickyFooterActions.text}
            />

            {FEATURE_FAST_CHECKOUT && !isParticipant && (
                <FastCheckoutPopup
                    open={openFastCheckout}
                    setOpen={setOpenFastCheckout}
                    handleClose={() => setOpenFastCheckout(false)}
                />
            )}
        </>
    );
};

export default Cart;
